import {
    ILocationResponse,
    LocationServiceSubscriptionUpdateRequest,
    LocationSubscriptionServiceType,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { DropdownInlineEditor } from 'components/inline_edit/DropdownInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useMemo } from 'react';

type Option = {
    title: string;
    value: LocationSubscriptionServiceType;
};

const options: Option[] = [
    { title: 'Sentry', value: LocationSubscriptionServiceType.Sentry },
    { title: 'Desk+', value: LocationSubscriptionServiceType.DeskPlus },
    { title: 'None', value: LocationSubscriptionServiceType.None },
    { title: 'Unknown', value: LocationSubscriptionServiceType.Unknown },
];

export type LocationSubscriptionServiceCellProps = Readonly<{
    rowData: ILocationResponse;
    onChange: () => void;
}>;

export function LocationSubscriptionServiceCell({
    rowData,
    onChange,
}: LocationSubscriptionServiceCellProps) {
    const { canEditLocationSubscriptionService } = usePermissions();
    const { showToast } = useNotificationContext();

    const selectedOption = useMemo(
        () => options.find((s) => s.value === rowData.Settings.SubscriptionService),
        [rowData.Settings.SubscriptionService],
    );

    const { mutate: updateSubscriptionService, isPending } = useMutation({
        meta: { name: 'update-location-service-subscription' },
        mutationFn: (subscription: Option | undefined) =>
            sdClient.updateLocationServiceSubscription({
                locationId: rowData.Id,
                body: new LocationServiceSubscriptionUpdateRequest({
                    SubscriptionService:
                        subscription?.value ?? LocationSubscriptionServiceType.Unknown,
                }),
            }),

        onSuccess: onChange,

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to save location subscription',
            }),
    });

    return (
        <DropdownInlineEditor<Option>
            options={options}
            isDisabled={isPending || !canEditLocationSubscriptionService}
            dropdownConfig={{
                renderLabel: ({ value }) => value?.title,
                dataKey: (item) => item.value,
                renderItem: ({ item }) => item.title,
                popup: { width: '20rem' },
            }}
            value={selectedOption}
            renderValue={(value) => value?.title}
            onConfirm={updateSubscriptionService}
        />
    );
}
